import Layout from '@/components/Layout';
import GuideCard from '@/components/GuideCard';
import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { Guide } from "@/utils/types";
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Modal, Box, Button, TextField, MenuItem, FormControl, InputLabel, Select, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getToken } from "next-auth/jwt";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';

interface IndexProps {
  isAdmin: boolean;
}

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const token = await getToken({ req: context.req });
  const isAdmin = token?.role === 'Admin';
  return {
    props: { isAdmin }, // Pass isAdmin to the page component
  };
};

const Page: NextPage<IndexProps> = ({ isAdmin }) => {
  const [guides, setGuides] = useState<Array<Guide>>([]);
  const [newGuideTitle, setNewGuideTitle] = useState("");
  const [newGuideSubtitle, setNewGuideSubtitle] = useState("");
  const [newGuideDescription, setNewGuideDescription] = useState("");
  const [newGuideURL, setNewGuideURL] = useState("");
  const [newGuideCategory, setNewGuideCategory] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [isNewSelected, setIsNewSelected] = useState(false);
  const [categories, setCategories] = useState([{ value: 'New Category', label: 'New Category' }]);
  const [selectedCategory, setSelectedCategory] = useState('');




// Fetch from the server whenever the state changes
useEffect(() => {
  const fetchData = async () => {
    const res = await fetch('/api/guides');
    const Guides = await res.json();
    setGuides(Guides);

  // Add categories to the categories array
  const newCategories = [...categories];
  Guides.forEach((guide: Guide) => {
    if (!newCategories.find((category) => category.value === guide.category)) {
      newCategories.push({ value: guide.category, label: guide.category });
    }
  });
  
  // Separate "New" and sort the rest
  const sortedCategories = newCategories
    .filter(category => category.value !== 'New Category')
    .sort((a, b) => a.value.localeCompare(b.value));

  setCategories([{ value: 'New Category', label: 'New Category' }, ...sortedCategories]);
  };

fetchData();
}, [refresh]);

const handleCategoryChange = (e: SelectChangeEvent) => {
  const value = e.target.value;
  setNewGuideCategory(value);
  setIsNewSelected(value === 'New Category');
};

const handleAdd = async () => {
  if (!newGuideTitle.trim()) {
    console.log('Link name is empty. Not adding.');
    return;
  }

  const category = customCategory ? customCategory : newGuideCategory;

  // Construct new Guide
  const Guide: Guide = {
    title: newGuideTitle,
    subtitle: newGuideSubtitle,
    description: newGuideDescription,
    url: newGuideURL,
    category: category,
    likes: 0,
  };

  // Make the API request
  await fetch('/api/guides', {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(Guide),
  });

  // Add custom category to the list if it doesn't already exist
  if (customCategory && !categories.find((c) => c.value === customCategory)) {
    setCategories((prevCategories) => [...prevCategories, { value: customCategory, label: customCategory }]);
  }

  // Clear the state
  setNewGuideTitle("");
  setNewGuideSubtitle("");
  setNewGuideDescription("");
  setNewGuideURL("");
  setNewGuideCategory("");
  setCustomCategory("");
  setIsNewSelected(false);

  // Refresh the links
  setRefresh(!refresh);
};

const handleDelete = async (id: number) => {
  await fetch('/api/guides', {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ _id: id }),
  });

  setRefresh(!refresh);
};

  return (
    <Layout>
      {/* <GuideCard data={GuideCardData}/>
      <br /> */}
      <div className="flex flex-col px-4" style={{ width: '100%' }}>
        <Typography sx={{ mt: 4, mb: 2 }} variant="h4" component="div">
          Guides
        </Typography>
        
        {isAdmin && (
          <>
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                Add Guide
              </AccordionSummary>
              <AccordionDetails>
              <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sx={{ mb: 3 }}>
                <TextField
                  sx={{ height: '0.5em', mt: -1 }}
                  autoFocus
                  id="title"
                  placeholder='Title'
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGuideTitle}
                  onChange={(e) => setNewGuideTitle(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sx={{ mb: 3 }}>
                <TextField
                  sx={{ height: '0.5em', mt: -1 }}
                  autoFocus
                  id="subtitle"
                  placeholder='Subtitle'
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGuideSubtitle}
                  onChange={(e) => setNewGuideSubtitle(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sx={{ mb: 3 }}>
                <TextField
                  sx={{ height: '0.5em', mt: -1 }}
                  id="description"
                  placeholder='Description'
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGuideDescription}
                  onChange={(e) => setNewGuideDescription(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sx={{ mb: 3 }}>
                <TextField
                  sx={{ height: '0.5em', mt: -1 }}
                  id="url"
                  placeholder='Video URL'
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGuideURL}
                  onChange={(e) => setNewGuideURL(e.target.value)}
                />
              </Grid>
            </Grid>            
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category"
                    variant="outlined"
                    value={newGuideCategory}
                    label="Category"
                    onChange={handleCategoryChange}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {isNewSelected && (
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} sx={{ mb: 3 }}>
                  <TextField
                    sx={{ height: '0.5em', mt: -1 }}
                    id="customCategory"
                    placeholder='Custom Category'
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Button
                  className="bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={handleAdd}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
              </AccordionDetails>
            </Accordion> 
          </>
        )}
        <Grid container sx={{ mb: 2 }} spacing={2} alignItems="center" justifyContent="flex-end">
          <Grid item>            
            <Select
               value={selectedCategory}
               onChange={(e) => setSelectedCategory(e.target.value)}
               displayEmpty
               inputProps={{ 'aria-label': 'Without label' }}
              style={{ 
                border: 'none', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '4px', 
                cursor: 'pointer',
              }}
            >
                <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {categories.filter(category => category.value !== 'New Category').map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        </div>
        {guides
          .filter((guide) => selectedCategory === '' || guide.category === selectedCategory)
          .map((guide, index) => (
            <div key={guide._id} style={{ display: 'flex', flexDirection: 'column' }}> {/* Ensure each guide has a unique key, using guide.id or similar */}
              <GuideCard data={{ title: guide.title, subtitle: guide.subtitle, description: guide.description, url: guide.url, category: guide.category }}/>
              {isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <Button                  
                    style={{ 
                      marginTop: '10px', 
                      backgroundColor: '#f44336', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      padding: '10px 20px'
                    }}
                    onClick={() => handleDelete(guide._id!)}
                  >
                    Delete
                  </Button>
                </div>
              )}
              <Divider sx={{ m: 2 }}></Divider>
            </div>
          ))
        }
    </Layout>
  );
};

export default Page;